import { titleCaseToken } from '../../../utils';
import { EMPTY_VALUE, LocationUserAdapters, UserAdapters } from '../../Users';

/**
 * A status slug as a label ("verified" → "Verified", "pending_verification" →
 * "Pending Verification"). No label map is mirrored because the
 * identity-status enum lives in TempMee/x, out of reach here; a value nobody
 * has seen yet still reads sensibly.
 */
const toSlugLabel = (slug) => (slug ? titleCaseToken(slug) : EMPTY_VALUE);

/**
 * Unwraps the listing envelope. A missing `identities` array reads as an empty
 * list rather than throwing.
 *
 * @param {import('../types/Identity.api').IGetLocationIdentitiesApiResponse} response
 * @returns {import('../types/Identity.api').IIdentityApiModel[]}
 */
const toIdentities = (response) => response?.identities ?? [];

/**
 * One table row. The phone and last activity are auth-service's, and the
 * location count organization-service's other read, so each is passed in;
 * an identity auth-service has no user for reads an em dash in both. Access
 * reads as it does on the Location User's Locations tab
 * (`LocationUserAdapters.toAccessLabels`).
 *
 * @param {import('../types/Identity.api').IIdentityApiModel} identity
 * @param {import('../../Users/types/User').IUserSummaryViewEntity | undefined} user
 * @param {string} locationCountLabel - See `UserAdapters.toLocationCountLabel`
 * @returns {import('../types/Identity').ILocationAdminRow}
 */
const toLocationAdminRow = (identity, user, locationCountLabel) => ({
  id: identity.identityId,
  name: identity.name || EMPTY_VALUE,
  email: identity.email || EMPTY_VALUE,
  phoneLabel: UserAdapters.toPhoneLabel(user?.phoneNumber),
  statusLabel: toSlugLabel(identity.identityStatus),
  locationCountLabel,
  ...LocationUserAdapters.toAccessLabels(identity.source),
  lastActiveLabel: UserAdapters.toLastActiveLabel(user?.lastActiveAt),
});

/**
 * The table rows, in the identities' order, each joined by id to its
 * auth-service user and its location count. Either read may not have answered
 * yet, or may have failed; the row stays either way.
 *
 * @param {import('../types/Identity.api').IIdentityApiModel[]} identities
 * @param {import('../../Users/types/User').IUserSummaryViewEntity[]} users
 * @param {Object<string, number>} countsById - an id with no entry has not loaded
 * @param {boolean} isCountsError - the counts read failed
 * @returns {import('../types/Identity').ILocationAdminRow[]}
 */
const toLocationAdminRows = (identities, users, countsById, isCountsError) => {
  const userById = new Map(users.map((user) => [user.id, user]));

  return identities.map((identity) => toLocationAdminRow(
    identity,
    userById.get(identity.identityId),
    UserAdapters.toLocationCountLabel(countsById[identity.identityId], isCountsError),
  ));
};

export const IdentityAdapters = {
  toIdentities,
  toLocationAdminRows,
};
